import { useState, type ReactNode } from 'react';
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  Code2,
  Loader2,
  MessageSquare,
  Receipt,
  Rocket,
  Search,
  Send,
  Shield,
  ShoppingBag,
  Store,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { useBrand } from '../brand/BrandProvider';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/cn';
import { useCurrentReseller } from '../api/reseller';

type Category = {
  slug: string;
  title: string;
  description: string;
  icon: ReactNode;
  iconBg: string;
  badge?: string;
  comingSoon?: boolean;
  articles: string[];
};

const CATEGORIES: Category[] = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    description: 'Onboarding guide and your first business setup walkthrough.',
    icon: <Rocket className="w-5 h-5" />,
    iconBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    articles: [
      'Create your first business',
      'Invite teammates and set roles',
      'Connect your domain',
      'Pick the right package',
      'Run your first sale',
    ],
  },
  {
    slug: 'billing',
    title: 'Billing & invoices',
    description: 'Subscriptions, plan changes, refunds and payment methods.',
    icon: <Receipt className="w-5 h-5" />,
    iconBg: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
    articles: [
      'Change your subscription plan',
      'Update payment method',
      'Request a refund',
      'Download an invoice',
      'Understand commission and gross/net',
    ],
  },
  {
    slug: 'pos-basics',
    title: 'POS basics',
    description: 'Sales, inventory, reports — running day-to-day operations.',
    icon: <ShoppingBag className="w-5 h-5" />,
    iconBg: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    articles: [
      'How to add a business location',
      'Refund a customer payment',
      'Read your daily summary report',
      'Manage stock across locations',
      'Set up barcode printing',
    ],
  },
  {
    slug: 'security',
    title: 'Security',
    description: 'Two-factor authentication, sessions, and password policy.',
    icon: <Shield className="w-5 h-5" />,
    iconBg: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    articles: [
      'Enable two-factor authentication',
      'Recover a lost authenticator',
      'Review active sessions',
      'Reset your password',
      'Spot a suspicious sign-in',
    ],
  },
  {
    slug: 'resellers',
    title: 'Resellers',
    description: 'Apply, brand your storefront, payouts, and commission.',
    icon: <Store className="w-5 h-5" />,
    iconBg: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    articles: [
      'Apply to become a reseller',
      'Customise your branding',
      'Request a payout',
      'Set price overrides per business',
      'View your commission history',
    ],
  },
  {
    slug: 'api',
    title: 'API docs',
    description: 'Public REST + webhooks for integrating your POS into your stack.',
    icon: <Code2 className="w-5 h-5" />,
    iconBg: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200',
    badge: 'Soon',
    comingSoon: true,
    articles: [
      'Authentication overview',
      'Subscriptions REST reference',
      'Webhook event types',
      'Rate limits and pagination',
      'SDKs and example code',
    ],
  },
];

/** Help & docs stub — search, categories, and a feedback form. */
export function Help() {
  const [search, setSearch] = useState('');
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [openArticle, setOpenArticle] = useState<{ category: string; title: string } | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackState, setFeedbackState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const brand = useBrand();
  const { data: currentReseller } = useCurrentReseller();
  const supportEmail = currentReseller?.supportEmail || brand.supportEmail;

  function sendFeedback() {
    if (!feedback.trim()) return;
    setFeedbackState('sending');
    window.setTimeout(() => {
      setFeedbackState('sent');
    }, 700);
  }

  function closeFeedback() {
    setFeedbackOpen(false);
    setTimeout(() => {
      setFeedback('');
      setFeedbackState('idle');
    }, 200);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <PageHeader
        eyebrow="Help & docs"
        title="How can we help?"
        subtitle="Browse articles, contact support, or check the API docs."
      />

      {/* Search */}
      <Card className="p-4 sm:p-5 mb-6">
        <Input
          placeholder="Search help articles…"
          prefix={<Search className="w-4 h-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search.trim().length > 0 && (
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">
            No results yet — try the categories below.
          </p>
        )}
      </Card>

      {/* Category grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {CATEGORIES.map((c) => {
          const isOpen = openSlug === c.slug;
          return (
            <Card key={c.slug} className="p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-3">
                <span className={cn('w-10 h-10 grid place-items-center rounded-md', c.iconBg)}>
                  {c.icon}
                </span>
                {c.badge && <Badge tone="neutral" dot={false}>{c.badge}</Badge>}
              </div>
              <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                {c.title}
              </h3>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300 flex-1">
                {c.description}
              </p>
              <button
                type="button"
                onClick={() => setOpenSlug(isOpen ? null : c.slug)}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-brand-dm hover:underline self-start"
                aria-expanded={isOpen}
              >
                Browse
                <ChevronDown
                  className={cn(
                    'w-4 h-4 transition-transform',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>

              {isOpen && (
                <ul className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 space-y-1">
                  {c.articles.map((a) => (
                    <li key={a}>
                      <button
                        type="button"
                        onClick={() => setOpenArticle({ category: c.title, title: a })}
                        className="w-full text-left text-sm py-1.5 px-2 -mx-2 rounded text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition flex items-center gap-2"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                        <span className="truncate">{a}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-zinc-400 ml-auto flex-shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>

      {/* Still need help */}
      <Card className="p-6 sm:p-8 text-center">
        <h2 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">
          Still need help?
        </h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
          Reach out to our support team or send us feedback — we read every message.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`mailto:${supportEmail}?subject=Help%20request`}
            className="inline-flex items-center justify-center font-medium rounded-md transition px-4 py-2.5 text-sm gap-2 bg-brand text-white shadow-xs hover:bg-brand-hover active:bg-brand-active dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            <MessageSquare className="w-4 h-4" />
            Contact support
          </a>
          <Button
            variant="secondary"
            iconLeft={<Send className="w-4 h-4" />}
            onClick={() => setFeedbackOpen(true)}
          >
            Submit feedback
          </Button>
        </div>
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400 break-all">
          Support email: <span className="mono">{supportEmail}</span>
        </p>
      </Card>

      {/* Article modal */}
      <Modal
        open={Boolean(openArticle)}
        onClose={() => setOpenArticle(null)}
        title={openArticle?.title ?? 'Article'}
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setOpenArticle(null)}>
            Close
          </Button>
        }
      >
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {openArticle?.category}
          </div>
          <p className="text-sm text-zinc-700 dark:text-zinc-200">
            Article content coming soon — for now, contact support.
          </p>
          <a
            href={`mailto:${supportEmail}?subject=${encodeURIComponent(openArticle?.title ?? 'Help')}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Email support
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </Modal>

      {/* Feedback modal */}
      <Modal
        open={feedbackOpen}
        onClose={closeFeedback}
        title="Send feedback"
        size="md"
        footer={
          feedbackState === 'sent' ? (
            <Button variant="primary" onClick={closeFeedback}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeFeedback}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={sendFeedback}
                disabled={feedbackState === 'sending' || !feedback.trim()}
                iconLeft={
                  feedbackState === 'sending' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )
                }
              >
                {feedbackState === 'sending' ? 'Sending…' : 'Send'}
              </Button>
            </>
          )
        }
      >
        {feedbackState === 'sent' ? (
          <div className="text-center py-2">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-3">
              <Send className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              Feedback sent
            </h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              Thanks — we read every message.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Tell us what's working, what isn't, or what you'd like next.
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              placeholder="Your feedback…"
              className={cn(
                'block w-full px-3 py-2.5 text-sm rounded-md transition resize-none',
                'bg-white text-zinc-950 placeholder-zinc-400 border border-zinc-300',
                'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                'dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500 dark:border-zinc-700',
                'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
